import React, { useState } from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  orderNumber: "",
  message: "",
  consent: false,
};

const contactFields = [
  {
    name: "firstName",
    label: "First name",
    autoComplete: "given-name",
    required: true,
  },
  {
    name: "lastName",
    label: "Last name",
    autoComplete: "family-name",
    required: true,
  },
  {
    name: "email",
    label: "Email address",
    type: "email",
    autoComplete: "email",
    required: true,
  },
];

const subjectOptions = [
  "Order status",
  "Product advice",
  "Returns and exchanges",
  "Press and partnerships",
  "Wholesale",
  "Something else",
];

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState(null);

  const handleInputChange = ({ target }) => {
    const { name, type, value, checked } = target;

    setFormData((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }

    if (formStatus) {
      setFormStatus(null);
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim()) {
      nextErrors.firstName = "Please enter your first name.";
    }
    if (!formData.lastName.trim()) {
      nextErrors.lastName = "Please enter your last name.";
    }
    if (!formData.email.trim()) {
      nextErrors.email = "Please enter your email address.";
    } else if (!emailPattern.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address.";
    }
    if (!formData.subject) {
      nextErrors.subject = "Please choose a subject.";
    }
    if (!formData.message.trim()) {
      nextErrors.message = "Please tell us how we can help.";
    } else if (formData.message.trim().length < 20) {
      nextErrors.message = "Please add a little more detail (at least 20 characters).";
    }
    if (!formData.consent) {
      nextErrors.consent = "Please confirm that we may use your details to reply.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setFormStatus({
        type: "error",
        message: "Some details need your attention before the message can be sent.",
      });
      const firstInvalidField = document.getElementById(
        `contact-${Object.keys(nextErrors)[0]}`
      );
      firstInvalidField?.focus();
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    setFormStatus(null);

    try {
      await new Promise((resolve) => window.setTimeout(resolve, 650));
      setFormData(initialFormData);
      setFormStatus({
        type: "success",
        message:
          "Thank you. Your message has been received and our studio team will reply as soon as possible.",
      });
    } catch {
      setFormStatus({
        type: "error",
        message: `We could not send your message. Please email ${siteConfig.contact.customerServiceEmail} instead.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="contact" id="main-content">
      <header className="contact__header">
        <p className="contact__eyebrow">Customer care</p>
        <h1>Let&rsquo;s talk.</h1>
        <p>
          Questions about a piece, an order or the studio? Send us a note and
          the {siteConfig.brandName} team will point you in the right direction.
        </p>
      </header>

      <div className="contact__layout">
        <aside className="contact__details" aria-label="Contact information">
          <section>
            <h2>Customer service</h2>
            <a href={`mailto:${siteConfig.contact.customerServiceEmail}`}>
              {siteConfig.contact.customerServiceEmail}
            </a>
            <p>{siteConfig.contact.hours}</p>
          </section>

          <section>
            <h2>Studio</h2>
            <address>{siteConfig.contact.address}</address>
            <p>Visits by appointment.</p>
          </section>

          <section>
            <h2>Wholesale</h2>
            <a href={`mailto:${siteConfig.contact.wholesaleEmail}`}>
              {siteConfig.contact.wholesaleEmail}
            </a>
          </section>

          <p className="contact__response-note">
            We usually reply within two business days. For an existing order,
            include the order number shown in your confirmation email.
          </p>
        </aside>

        <section className="contact__form-section" aria-labelledby="contact-form-title">
          <div className="contact__form-heading">
            <p className="contact__eyebrow">Send a message</p>
            <h2 id="contact-form-title">How can we help?</h2>
          </div>

          <form className="contact__form" onSubmit={handleSubmit} noValidate>
            {formStatus && (
              <div
                className={`contact__status contact__status--${formStatus.type}`}
                role={formStatus.type === "error" ? "alert" : "status"}
                tabIndex="-1"
              >
                {formStatus.message}
              </div>
            )}

            <div className="contact__field-grid">
              {contactFields.map((field) => (
                <div className="contact__field" key={field.name}>
                  <label htmlFor={`contact-${field.name}`}>
                    {field.label}
                    {field.required && <span aria-hidden="true"> *</span>}
                  </label>
                  <input
                    id={`contact-${field.name}`}
                    name={field.name}
                    type={field.type || "text"}
                    value={formData[field.name]}
                    onChange={handleInputChange}
                    autoComplete={field.autoComplete}
                    required={field.required}
                    aria-invalid={Boolean(errors[field.name])}
                    aria-describedby={
                      errors[field.name] ? `contact-${field.name}-error` : undefined
                    }
                  />
                  {errors[field.name] && (
                    <span
                      className="contact__field-error"
                      id={`contact-${field.name}-error`}
                    >
                      {errors[field.name]}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className="contact__field-grid contact__field-grid--context">
              <div className="contact__field">
                <label htmlFor="contact-subject">
                  Subject <span aria-hidden="true">*</span>
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  aria-invalid={Boolean(errors.subject)}
                  aria-describedby={errors.subject ? "contact-subject-error" : undefined}
                >
                  <option value="">Choose a subject</option>
                  {subjectOptions.map((subject) => (
                    <option value={subject} key={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
                {errors.subject && (
                  <span className="contact__field-error" id="contact-subject-error">
                    {errors.subject}
                  </span>
                )}
              </div>

              <div className="contact__field">
                <label htmlFor="contact-orderNumber">Order number (optional)</label>
                <input
                  id="contact-orderNumber"
                  name="orderNumber"
                  value={formData.orderNumber}
                  onChange={handleInputChange}
                  autoComplete="off"
                  placeholder="e.g. 1048"
                />
              </div>
            </div>

            <div className="contact__field contact__field--message">
              <label htmlFor="contact-message">
                Message <span aria-hidden="true">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                required
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "contact-message-error" : undefined}
              />
              <span className="contact__character-count" aria-live="polite">
                {formData.message.length} characters
              </span>
              {errors.message && (
                <span className="contact__field-error" id="contact-message-error">
                  {errors.message}
                </span>
              )}
            </div>

            <div className="contact__consent">
              <input
                id="contact-consent"
                name="consent"
                type="checkbox"
                checked={formData.consent}
                onChange={handleInputChange}
                required
                aria-invalid={Boolean(errors.consent)}
                aria-describedby={errors.consent ? "contact-consent-error" : undefined}
              />
              <label htmlFor="contact-consent">
                I agree that my details may be used to answer this enquiry. See
                our <Link to="/privacy">privacy policy</Link>.
              </label>
              {errors.consent && (
                <span className="contact__field-error" id="contact-consent-error">
                  {errors.consent}
                </span>
              )}
            </div>

            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending…" : "Send message"}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}

export default Contact;
